import { FeatureFlagIds } from "../../../../utils/featureFlagIds";
import React, {useEffect, useState} from 'react'
import {DialogActions, DialogContent, DialogTitle,} from "@mui/material";
import {useDispatch, useSelector} from 'react-redux';
import {
  ApplicationQuoteType, 
  usesFinanceQuote,
  FinanceQuoteNonUniformPaymentOption,
  hasValue,
  PermissionSet,
  useDebounce,
  DatePickerWithYearSanitizer
} from "@amsanalytics/react-shared-library";
import {Error} from "@progress/kendo-react-labels";
import {Input, InputChangeEvent, NumericTextBox} from "@progress/kendo-react-inputs";
import {ComboBox, ComboBoxChangeEvent} from "@progress/kendo-react-dropdowns";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import {DatePickerChangeEvent} from "@progress/kendo-react-dateinputs";
import produce from "immer";
import styles from './_ams-loan-posting-dialog.module.scss';
import {IConfiguration } from '../../../../Data/ConfigurationData';
import { ILookupItems } from '../../../../Interfaces/ILookupItems';
import {IPostAmsLoan, ICpiDetails, IPreviousAmsCustomerDetailWithNames } from '../../../../Interfaces/Common/IPostAmsLoan';
import { useBusinessRulesEngine } from '../../../../Data/BusinessRulesData';
import {ApplicationDispatch} from '../../../../store/application/types';
import { AppState } from '../../../../store/rootReducer';
import { AmsCustomerPreCheckSearchResult } from '../../../../Interfaces/Common/AmsCustomerPreCheckSearchResult';
import { updateEditingApp } from '../../../../store/application/actions';
import {uniq} from "ramda";
import {PassTimeUnitType} from "../../../../Interfaces/Common/PassTimeUnitType";
import {DialogProps} from "../../../Dialogs/DialogProps";
import {validateStockNumber} from "../../../../Data/VehicleData";
import {getAmsCodeForReserve} from "../../../Quoters/IndirectQuoter/IndirectQuoterUtils";
import {IApplication} from "../../../../Interfaces/Application/IApplication";
import {
  AmsLookupForComboBoxDisplay,
  getAmsCustomersForAmsCustomerNumbersAsync,
  getAmsLateFeesToDisplay,
  getAmsLateFeeToDisplay,
  getAmsLocationsOfSaleToDisplay,
  getAmsLookupForComboBoxDisplay,
  getAmsLookupsToDisplay,
  getAmsLookupToDisplay,
  getAmsUsersToDisplay,
  getAmsUserToDisplay,
  getAtLeastOneCustomerNumberIsTooLongError,
  getDefaultCustomerDetailsForPosting,
  getLosCustomerIdAndAmsCustomerNumberPairs,
  maxAmsAccountNumberLength,
  maxAmsCustomerNumberLength,
} from './AmsLoanPostingUtils';
import {CustomerNumberInputsDialog} from "../../../Dialogs/CustomerNumberInputs";
import {getAssignedEmployeeByRoleAndSequence} from "../../../../utils/appUtils";
import {Constants, permissions} from "../../../../utils/constants";
import {userHasPermission} from "../../../../utils/utils";
import {AmsLoanCpiDialog} from "../AmsLoanCpiDialog/AmsLoanCpiDialog";
import {getSelectedFinanceQuote} from "../../../../utils/quoteUtils";
import { Button } from '@progress/kendo-react-buttons';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';
import { httpGetAsync } from '../../../../utils/HttpClient';
import { authService } from '../../../../index';

interface AmsLoanPostingDialogProps extends DialogProps<null> {
  onOK: (appToSave: IApplication) => void;
  configuration: IConfiguration;  
  lookups: ILookupItems;
  applicationOrganizationId: string;
}

export const AmsLoanPostingDialog: React.FC<AmsLoanPostingDialogProps> = (props) => {
  const businessRulesEngine = useBusinessRulesEngine();
  const lookups = props.lookups;
  const amsLoanPostingDialogDefaultCodes = props.configuration.amsLoanPostingDialogDefaultCodes;
  const dispatch = useDispatch<ApplicationDispatch>();
  const currentApp = useSelector((state: AppState) => state.applications.editingApp!);
  const selectedFinanceQuote = getSelectedFinanceQuote(currentApp);
  const cpiConfigForPaymentFrequency = lookups.cpiConfigs.find(cpi => cpi.paymentFrequency === selectedFinanceQuote?.paymentFrequency);
  const defaultCpiFactorAmount =  cpiConfigForPaymentFrequency?.factorAmount ?? 0;
  const employees = useSelector((state: AppState) => state.applications.employees);
  const activeUser = useSelector((state: AppState) => state.applications.loggedInUser?.employee);
  const [postLoanDetails, setPostLoanDetails] = useState<IPostAmsLoan | null>(currentApp.postLoanDetails);
  const debouncedPostLoanDetails = useDebounce(postLoanDetails, 500); // Wait 500ms after user stops editing post loan details

  useEffect(() => {
    // Updates the post loan details from the debounced data
    const updatedApp = produce(currentApp!, updatedCurrentApp => {
      updatedCurrentApp.postLoanDetails = postLoanDetails;
    });
    dispatch(updateEditingApp(updatedApp, businessRulesEngine));
  }, [debouncedPostLoanDetails]);
 
  const [loading, setLoading] = useState<boolean>(false);
  const [showCpiDialog, setShowCpiDialog] = useState<boolean>(false);
  const [showInsuranceMismatchWarning, setShowInsuranceMismatchWarning] = useState<boolean>(false);
  const [insuranceMismatchMessages, setInsuranceMismatchMessages] = useState<string[]>([]);
  const [usePassTimeForLoan, setUsePassTimeForLoan] = useState<boolean>(false);
  const clientUsesPassTime = props.configuration.usePassTime;
  const clientUsesCpi = props.configuration.useCpi;
  const usePassTimeForLoanOptions = [
    { usePassTimeForLoan: true, label: "YES" },
    { usePassTimeForLoan: false, label: "NO" }
    ];
  
  const applyLateFeeToDeferredDownOptions = [
    { value: true, label: "YES" },
    { value: false, label: "NO" }
  ];
  const hasDeferredDownPayments = usesFinanceQuote(currentApp.quoteType)
    ? (selectedFinanceQuote?.financeQuoteDeferredPayments?.length ?? 0) > 0
    : currentApp.quoteType === ApplicationQuoteType.Lease
      ? hasValue(currentApp.applicationLeaseQuote?.deferredPmtDate1)
      : false;

  const passTimeUnitTypes = props.lookups.passTimeUnitTypes;
  const passTimeConfigs = props.lookups.passTimeConfigs;
  
  const updateUsePassTimeForLoan = (usePassTime: boolean) => {
    if (!postLoanDetails) {
      return;
    }
    
    const amsPassTimeDefaultUnitForOrganization = passTimeConfigs.find(ptc => hasValue(ptc.organizationAmsCode) && ptc.organizationAmsCode.trim() === organizationForApplication?.amsCode?.trim());
    const defaultAmsPassTimeDefaultUnitForOrganization = passTimeUnitTypes.find(unit => unit.combinedTypeEnum === amsPassTimeDefaultUnitForOrganization?.defaultCombinedTypeEnum);
    
    setPostLoanDetails({
      ...postLoanDetails,
      passTimeUnitCombinedEnum: !usePassTime
        ? undefined
        : defaultAmsPassTimeDefaultUnitForOrganization?.combinedTypeEnum,
      passTimeSerialNumber: !usePassTime
        ? undefined
        : postLoanDetails.passTimeSerialNumber
    });
    setUsePassTimeForLoan(usePassTime);
  }

  const trimAndTruncateString = (inputString: string | undefined, length: number) => {
    return inputString?.trim().slice(0, length);
  }

  const [preCheckSearchResults, setPreCheckSearchResults] = useState<AmsCustomerPreCheckSearchResult[]>([]);
  const [stockNumberError, setStockNumberError] = useState<string | null>(null);

  const lastnamesDifferBetweenApplicantAndSelectedPreviousAmsCustomer = (selectedPreviousCustomerFromAms: IPreviousAmsCustomerDetailWithNames, lastName: string): boolean => {
    return !!selectedPreviousCustomerFromAms && !selectedPreviousCustomerFromAms.optionForManualEntry && (lastName.toUpperCase().trim() !== selectedPreviousCustomerFromAms.lastName.toUpperCase().trim());
  }

  const desiredAmsCustomerNumbers = postLoanDetails?.customers.map(acd => acd.amsCustomerNumber?.trim().toUpperCase()) ?? [];
  const anAmsOrLosCustomerAlreadyExistsForADesiredNumber = preCheckSearchResults.some(ac => 
    desiredAmsCustomerNumbers.includes(ac.queriedAmsCustomerNumber.toUpperCase().trim()) && hasValue(ac.existingAmsCustomerString) ||  hasValue(ac.existingLosCustomerString));
  
  const atLeastOneCustomerDoesntHaveNumberEntered = postLoanDetails?.customers.some(c => !hasValue(c.amsCustomerNumber));
  const atLeastOneCustomerDoesntHaveAcknowledgedNameDifference = postLoanDetails?.customers.some(c =>
    lastnamesDifferBetweenApplicantAndSelectedPreviousAmsCustomer(c.selectedPreviousCustomerFromAms, c.lastName) && !c.confirmSelectedNameIsDifferentThanApplicant) ?? false;
  const usingPassTimeWithoutUnit = usePassTimeForLoan && !hasValue(postLoanDetails?.passTimeUnitCombinedEnum);
  const usingPassTimeWithoutSerialNumber = usePassTimeForLoan && !hasValue(postLoanDetails?.passTimeSerialNumber);
  // If the same value exists more than once, the unique number of customer numbers will be less than the number of customers
  const thereAreMultipleCustomersWithTheSameAmsCustomerNumber = !!postLoanDetails && (postLoanDetails.customers.length > uniq(postLoanDetails.customers.map(c => c.amsCustomerNumber)).length);

  // Compute effective ACV (mirrors Vehicle.GetAcvForAmsPurposes on backend)
  const bookoutValue = currentApp.vehicle?.bookoutValue ?? 0;
  const vehicleAcv = currentApp.vehicle?.acv ?? 0;
  const effectiveAcv = bookoutValue > 0
    ? bookoutValue
    : vehicleAcv > 0
      ? vehicleAcv
      : 0;
  const vehicleAcvIsMissingOrZero = !effectiveAcv || effectiveAcv <= 0;

  const errors: string[] = [];

  if (stockNumberError) {
    errors.push(stockNumberError);
  }

  if (currentApp.vehicle && vehicleAcvIsMissingOrZero) {
    errors.push("Vehicle ACV or Bookout Value must be greater than $0 before posting. Please update the ACV or Bookout Value on the vehicle.");
  }
  if (anAmsOrLosCustomerAlreadyExistsForADesiredNumber) {
    errors.push("See error listed below applicant");
  }
  if (atLeastOneCustomerDoesntHaveNumberEntered) {
    errors.push("One of the applicants doesn't have a desired AMS Customer # yet");
  }
  if (atLeastOneCustomerDoesntHaveAcknowledgedNameDifference) {
    errors.push("Please acknowledge the name difference between the selected previous AMS customer and the LOS applicant");
  }
  if (usingPassTimeWithoutUnit) {
    errors.push("A PassTime unit type is needed if using PassTime");
  }
  if (usingPassTimeWithoutSerialNumber) {
    errors.push("A PassTime serial number is needed if using PassTime");
  }
  const thereAreMultipleCustomersWithTheSameAmsCustomerNumberMessage = "Each customer number should be unique";
  if (thereAreMultipleCustomersWithTheSameAmsCustomerNumber) {
    errors.push(thereAreMultipleCustomersWithTheSameAmsCustomerNumberMessage);
  }

  if (postLoanDetails && postLoanDetails.accountNumber.length > maxAmsAccountNumberLength) {
    errors.push(`The account number is longer than the max- ${maxAmsAccountNumberLength}`);
  }

  const atLeastOneCustomerNumberIsTooLongError = getAtLeastOneCustomerNumberIsTooLongError(postLoanDetails?.customers, maxAmsCustomerNumberLength);
  if (hasValue(atLeastOneCustomerNumberIsTooLongError)) {
    errors.push(atLeastOneCustomerNumberIsTooLongError!);
  }

  const organizationForApplication = lookups.organizations.find(o => o.id === props.applicationOrganizationId); 
  if (!hasValue(organizationForApplication?.amsCode)) {
    errors.push("Please exit this dialog and select a location for the application that is in AMS");
  }

  const salesPerson1AndCommissionAreValid = postLoanDetails?.salesman1CommissionAmount === 0 || hasValue(postLoanDetails?.salesman1EmployeeNumber);
  if (!salesPerson1AndCommissionAreValid) {
    errors.push("You must select a salesperson 1 if there is a commission amount for salesperson 1");
  }

  // SSN validation - check if any customer is missing SSN
  const customersWithMissingSsn = postLoanDetails?.customers.filter(c => !hasValue(c.ssn?.trim())) ?? [];
  if (customersWithMissingSsn.length > 0) {
    const customerNames = customersWithMissingSsn.map(c => `${c.firstName} ${c.lastName}`).join(", ");
    errors.push(`The following customer(s) are missing SSN: ${customerNames}. Please set their SSN before posting the loan.`);
  }

  const getAlphaNumericCharactersFromString = (value: string) => {
    return value.replace(/[^a-zA-Z0-9]/g, '');
  }

  // Fires whenever user enters a number into the input field.
  const handleDesiredCustomerNumberUpdate = (customerId: string, value: string): void => {
    if (!postLoanDetails) {
      return;
    }
    const filteredValue = getAlphaNumericCharactersFromString(value);
    
    setPostLoanDetails({
      ...postLoanDetails,
      customers: postLoanDetails.customers.map(c => {
        if (c.customerId === customerId) {
          return {
            ...c,
            amsCustomerNumber: trimAndTruncateString(filteredValue, maxAmsCustomerNumberLength) ?? ''
          }
        } else {
          return c;
        }
      })
    });
  };

  const handleAccountNumberUpdate = (value: string): void => {
    if (!postLoanDetails) {
      return;
    }
    const filteredValue = getAlphaNumericCharactersFromString(value);
    
    setPostLoanDetails({
      ...postLoanDetails,
      accountNumber: trimAndTruncateString(filteredValue, maxAmsAccountNumberLength) ?? ""
    });
  };

  const handleSelectCustomerNumberFromAms = (customerId: string, selectedAmsCustomerDetail: IPreviousAmsCustomerDetailWithNames): void => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      customers: postLoanDetails.customers.map(c => {
        if (c.customerId === customerId) {
          const newCustomerDetail = {
            ...c,
            selectedPreviousCustomerFromAms: selectedAmsCustomerDetail,
            amsCustomerNumber: trimAndTruncateString(selectedAmsCustomerDetail.amsCustomerNumber, maxAmsCustomerNumberLength) ?? '',
            updateInsteadOfCreate: !selectedAmsCustomerDetail.optionForManualEntry
          };

          if (lastnamesDifferBetweenApplicantAndSelectedPreviousAmsCustomer(selectedAmsCustomerDetail, c.lastName)) {
            newCustomerDetail.confirmSelectedNameIsDifferentThanApplicant = false;
          }

          return newCustomerDetail;
        } else {
          return c;
        }
      })
    });
  };

  const handleNameDifferenceAcknowledgement = (customerId: string, value: boolean): void => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      customers: postLoanDetails.customers.map(c => {
        if (c.customerId === customerId) {
          return {
            ...c,
            confirmSelectedNameIsDifferentThanApplicant: value,
          }
        } else {
          return c;
        }
      })
    });
  };

  const locationsOfPaymentsToDisplay = getAmsLookupsToDisplay(lookups.locationOfPayments);
  const locationsOfPurchaseToDisplay = getAmsLookupsToDisplay(lookups.vehicleLocationsOfPurchase);
  const amsDealerNumbersToDisplay = getAmsLookupsToDisplay(lookups.dealerNumbers);
  const lateFeesToDisplay = getAmsLateFeesToDisplay(lookups.amsLateFees);
  const loanCodesToDisplay = getAmsLookupsToDisplay(lookups.loanCodes);
  const loanTypesToDisplay = getAmsLookupsToDisplay(lookups.loanTypes);
  const accountOwnersToDisplay = getAmsLookupsToDisplay(lookups.accountOwners);
  const amsUsersToDisplay = getAmsUsersToDisplay(lookups.amsUsers);
  const advertisingOptionsToDisplay = getAmsLookupsToDisplay(lookups.advertisings);
  const portfoliosToDisplay = getAmsLookupsToDisplay(lookups.portfolios);
  const receiptMessagesToDisplay = getAmsLookupsToDisplay(lookups.receiptMessages);
  const interestTypesToDisplay = getAmsLookupsToDisplay(lookups.interestTypes);
  
  const amsLocationOfSaleCodeForApp = getAmsCodeForReserve(currentApp, lookups.organizations);
  const selectedLocationOfSale = lookups.organizations.find(o => o.amsCode === amsLocationOfSaleCodeForApp || o.reserveAmsCode === amsLocationOfSaleCodeForApp);
  const selectedLocationOfSaleToDisplay = getAmsLookupForComboBoxDisplay(selectedLocationOfSale?.id, amsLocationOfSaleCodeForApp, selectedLocationOfSale?.name);
  
  // If they have an indirect quote and the funding module exists, get the loan type ID
  const appQuoteTypeIsIndirect = (currentApp.quoteType === ApplicationQuoteType.Indirect);
  const loanTypeFromFundingModule = appQuoteTypeIsIndirect && hasValue(currentApp.applicationFunding?.loanTypeId)
    ? loanTypesToDisplay.find(lt => lt.id === currentApp.applicationFunding!.loanTypeId)
    : undefined;

  const vehicleHasLocationOfPurchase = hasValue(currentApp!.vehicle?.locationOfPurchaseId);
  
  const getAmsEmployeeNumber = (roleId: string, sequence: number): string | undefined => {
    const losEmployeeId = getAssignedEmployeeByRoleAndSequence(currentApp.applicationAssignments, roleId, sequence)?.assignedToId;
    const losEmployee = employees.find(e => e.id === losEmployeeId);
    
    // This step makes sure that the employee number is in AMS 2000. Make sure it won't crash when the loan is posted.
    return amsUsersToDisplay.find(u => u.code === losEmployee?.employeeNumber)?.code;
  };
  
  useEffect(() => { // used to create the object when opened for the first time
    if (postLoanDetails) {
      return;
    }
    
    const defaultLocationOfPurchase = locationsOfPurchaseToDisplay.find(lp => lp.id === amsLoanPostingDialogDefaultCodes?.locationOfPurchaseId);
    const defaultLocationOfPayment = locationsOfPaymentsToDisplay.find(lop => lop.code === amsLoanPostingDialogDefaultCodes?.locationOfPaymentCode);
    const defaultDealerNumber = amsDealerNumbersToDisplay.find(dn => dn.code === amsLoanPostingDialogDefaultCodes?.dealerNumberCode);
    const defaultLateFee = lateFeesToDisplay.find(lf => lf.code === amsLoanPostingDialogDefaultCodes?.lateFeeCode);
    const defaultLoanCode = loanCodesToDisplay.find(lc => lc.code === amsLoanPostingDialogDefaultCodes?.loanCode);
    const defaultLoanType = loanTypesToDisplay.find(lt => lt.code === amsLoanPostingDialogDefaultCodes?.loanTypeCode);
    const defaultLoanOwner = accountOwnersToDisplay.find(ao => ao.code === amsLoanPostingDialogDefaultCodes?.loanOwnerCode);
    const defaultAdvertising = advertisingOptionsToDisplay.find(ad => ad.code === amsLoanPostingDialogDefaultCodes?.advertisingCode);
    const defaultPortfolioNumber = portfoliosToDisplay.find(p => p.code === amsLoanPostingDialogDefaultCodes?.portfolioNumberCode);
    const defaultReceiptMessage = receiptMessagesToDisplay.find(r => r.code === amsLoanPostingDialogDefaultCodes?.receiptMessageCode);
    const defaultCollectorEmployee = amsUsersToDisplay.find(u => u.code === amsLoanPostingDialogDefaultCodes?.collectorEmployeeNumber);
    const defaultInterestType = interestTypesToDisplay.find(it => it.code === amsLoanPostingDialogDefaultCodes?.interestTypeCode);
    
    const salesperson1EmployeeNumber = getAmsEmployeeNumber(Constants.roles.salespersonId, 1);
    const salesperson2EmployeeNumber = getAmsEmployeeNumber(Constants.roles.salespersonId, 2);

    const multiplePaymentStreamsExist = usesFinanceQuote(currentApp.quoteType)
      && (selectedFinanceQuote?.financeQuoteIrregularPaymentStreams.length ?? 0) > 0
      && selectedFinanceQuote?.nonUniformPaymentOption === FinanceQuoteNonUniformPaymentOption.IrregularPayments;
    const multiplePaymentLoanTypeToUse: string | undefined = multiplePaymentStreamsExist
      ? currentApp.vehicle
        // If it has a vehicle, use "MP". If no vehicle, use "MPS". The S stands for side account.
        ? lookups.loanTypes.find(lc => lc.name === "MP")?.name
        : lookups.loanTypes.find(lc => lc.name === "MPS")?.name
      : undefined;

    setPostLoanDetails({
      accountNumber: "1",
      customers: getDefaultCustomerDetailsForPosting(currentApp, props.configuration.amsLoanPostingDialogDefaultCodes?.customerNumberCreationMethod),
      // For each of the section below, it tries to grab the default. If there is no default, it just grabs the first one. If there are none, it's an empty string and shows an error.
      locationOfPaymentCode: defaultLocationOfPayment?.code
        ?? (lookups.locationOfPayments.length > 0
          ? getAmsLookupToDisplay(lookups.locationOfPayments[0]).code
          : ''),
      locationOfSaleCode: amsLocationOfSaleCodeForApp ?? '',
      locationOfPurchaseId: currentApp!.vehicle?.locationOfPurchaseId
        ?? defaultLocationOfPurchase?.id
        ?? (lookups.vehicleLocationsOfPurchase.length > 0 ? getAmsLookupToDisplay(lookups.vehicleLocationsOfPurchase[0]).id : ""),
      dealerNumberCode: defaultDealerNumber?.code
        ?? (lookups.dealerNumbers.length > 0
          ? getAmsLookupToDisplay(lookups.dealerNumbers[0]).code
          : ''),
      lateFeeCode: defaultLateFee?.code
        ?? (lookups.amsLateFees.length > 0
          ? getAmsLateFeeToDisplay(lookups.amsLateFees[0]).code
          : ''),
      loanCode: defaultLoanCode?.code
        ?? (lookups.loanCodes.length > 0
          ? getAmsLookupToDisplay(lookups.loanCodes[0]).code
          : ''),
      loanTypeCode: multiplePaymentLoanTypeToUse
        ?? loanTypeFromFundingModule?.code
        ?? defaultLoanType?.code
        ?? (lookups.loanTypes.length > 0
          ? getAmsLookupToDisplay(lookups.loanTypes[0]).code
          : ''),
      loanOwnerCode: defaultLoanOwner?.code
        ?? (lookups.accountOwners.length > 0
          ? getAmsLookupToDisplay(lookups.accountOwners[0]).code
          : ''),
      advertisingCode: defaultAdvertising?.code
        ?? (lookups.advertisings.length > 0
          ? getAmsLookupToDisplay(lookups.advertisings[0]).code
          : ''),
      portfolioNumberCode: defaultPortfolioNumber?.code
        ?? (lookups.portfolios.length > 0
          ? getAmsLookupToDisplay(lookups.portfolios[0]).code
          : ''),
      salesLogDate: new Date(),
      receiptMessageCode: defaultReceiptMessage?.code
        ?? (lookups.receiptMessages.length > 0
          ? getAmsLookupToDisplay(lookups.receiptMessages[0]).code
          : ''),
      collectorEmployeeNumber: defaultCollectorEmployee?.code
        ?? (lookups.amsUsers.length > 0
          ? getAmsUserToDisplay(lookups.amsUsers[0]).code
          : ''),
      interestTypeCode: defaultInterestType?.code ?? 'S',
      passTimeUnitCombinedEnum: undefined,
      passTimeSerialNumber: undefined,
      autoAccrueOneCpiPaymentWhenPosting: false,
      loanCpiDetails: null,
      deferredDownCpiDetails: null,
      applyLateFeeScheduleToDeferredDown: false,
      salesman1EmployeeNumber: salesperson1EmployeeNumber,
      salesman2EmployeeNumber: salesperson2EmployeeNumber,
      salesman1CommissionAmount: props.configuration.featureFlags[FeatureFlagIds.UseCommission]
        ? props.configuration.defaultSalesperson1Commission
        : 0,
    });
  }, []);
  
  const updateSelectedLocationOfPayment = (locationOfPaymentLookup: AmsLookupForComboBoxDisplay | undefined) => {
    if (!postLoanDetails) {
      return;
    }
    
    setPostLoanDetails({
      ...postLoanDetails,
      locationOfPaymentCode: locationOfPaymentLookup?.code ?? ""
    });
  }

  const updateSelectedLocationOfPurchase = (locationOfPurchaseLookup: AmsLookupForComboBoxDisplay | undefined) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      locationOfPurchaseId: locationOfPurchaseLookup?.id ?? ""
    });
  }
  
  const updateSelectedDealerNumber = (dealerNumberLookup: AmsLookupForComboBoxDisplay | undefined) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      dealerNumberCode: dealerNumberLookup?.code ?? ""
    });
  }
  
  const updateSelectedLateFee = (lateFeeLookup: AmsLookupForComboBoxDisplay | undefined) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      lateFeeCode: lateFeeLookup?.code ?? ""
    });
  }
  
  const updateSelectedLoanCode = (loanCodeLookup: AmsLookupForComboBoxDisplay | undefined) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      loanCode: loanCodeLookup?.code ?? ""
    });
  }
  
  const updateSelectedLoanType = (loanTypeLookup: AmsLookupForComboBoxDisplay | undefined) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      loanTypeCode: loanTypeLookup?.code ?? ""
    });
  }
  
  const updateSelectedAccountOwner = (accountOwnerLookup: AmsLookupForComboBoxDisplay | undefined) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      loanOwnerCode: accountOwnerLookup?.code ?? ""
    });
  }
  
  const updateSelectedAdvertising = (advertisingLookup: AmsLookupForComboBoxDisplay | undefined) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      advertisingCode: advertisingLookup?.code ?? ""
    });
  }

  const updateSelectedPortfolio = (portfolioLookup: AmsLookupForComboBoxDisplay | undefined) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      portfolioNumberCode: portfolioLookup?.code ?? ""
    });
  }

  const updateSelectedSalesLogDate = (salesLogDate: Date | null) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      salesLogDate: salesLogDate ?? new Date()
    });
  }

  const updateSelectedReceiptMessage = (receiptMessageLookup: AmsLookupForComboBoxDisplay | undefined) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      receiptMessageCode: receiptMessageLookup?.code ?? ""
    });
  }

  const updateSelectedCollector = (collectorLookup: AmsLookupForComboBoxDisplay | undefined) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      collectorEmployeeNumber: collectorLookup?.code ?? ""
    });
  }

  const updateSalesperson1 = (userLookup: AmsLookupForComboBoxDisplay | undefined) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      salesman1EmployeeNumber: userLookup?.code ?? ""
    });
  }

  const updateSalesperson2 = (userLookup: AmsLookupForComboBoxDisplay | undefined) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      salesman2EmployeeNumber: userLookup?.code ?? ""
    });
  }

  const updateSalesperson1Commission = (salesperson1CommissionAmount: number) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      salesman1CommissionAmount: salesperson1CommissionAmount
    });
  }

  const updatePassTimeUnitCombinedEnum = (passTimeUnitType: PassTimeUnitType | undefined) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      passTimeUnitCombinedEnum: passTimeUnitType?.combinedTypeEnum
    });
  }

  const updatePassTimeUnitSerialNumber = (serialNumber: string | undefined) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      passTimeSerialNumber: serialNumber
    });
  }

  const updateSelectedInterestType = (interestTypeLookup: AmsLookupForComboBoxDisplay | undefined) => {
    if (!postLoanDetails) {
      return;
    }

    setPostLoanDetails({
      ...postLoanDetails,
      interestTypeCode: interestTypeLookup?.code ?? ""
    });
  }
  
  const atLeastOneDropDownIsMissing = !postLoanDetails
    || !hasValue(postLoanDetails.locationOfSaleCode)
    || (!vehicleHasLocationOfPurchase && !hasValue(postLoanDetails.locationOfPurchaseId))
    || !hasValue(postLoanDetails.locationOfPaymentCode)
    || !hasValue(postLoanDetails.dealerNumberCode)
    || !hasValue(postLoanDetails.lateFeeCode)
    || !hasValue(postLoanDetails.loanCode)
    || !hasValue(postLoanDetails.loanTypeCode)
    || !hasValue(postLoanDetails.loanOwnerCode)
    || !hasValue(postLoanDetails.advertisingCode)
    || !hasValue(postLoanDetails.portfolioNumberCode)
    || !hasValue(postLoanDetails.salesLogDate)
    || !hasValue(postLoanDetails.receiptMessageCode)
    || !hasValue(postLoanDetails.collectorEmployeeNumber)
    || !hasValue(postLoanDetails.interestTypeCode);

  // Check if any insurance record's stock number or VIN mismatches the application vehicle
  const getInsuranceMismatchWarnings = async (): Promise<string[]> => {
    const warnings: string[] = [];
    const vehicle = currentApp?.vehicle;
    if (!vehicle) return warnings;

    const allInsurances = currentApp?.appCustomers?.flatMap(ac => ac.appCustomerVehicleInsurances ?? []) ?? [];
    for (const insurance of allInsurances) {
      if (hasValue(insurance.stockNumber) && hasValue(vehicle.stockNumber)
        && insurance.stockNumber!.trim().toUpperCase() !== vehicle.stockNumber.trim().toUpperCase()) {
        warnings.push(`The insurance stock number (${insurance.stockNumber}) does not match the vehicle stock number (${vehicle.stockNumber}) on this deal.`);
      }
    }

    // Check VIN mismatch from Modives verification
    const hasVerifiedInsurance = allInsurances.some(ins => hasValue(ins.insuranceVerificationRequestId));
    if (hasVerifiedInsurance && hasValue(vehicle.vin)) {
      try {
        const bearerToken = await authService.getToken();
        const response = await httpGetAsync(
          `api/insuranceVerification/getLatestCompleteInsuranceDetail/${currentApp.id}`,
          bearerToken!
        );
        if (response) {
          const verificationData = await response.json();
          if (verificationData?.vin && verificationData.hasError !== true) {
            const verificationVin = verificationData.vin.trim().toUpperCase();
            const vehicleVin = vehicle.vin.trim().toUpperCase();
            if (verificationVin !== vehicleVin) {
              warnings.push(`The insurance VIN (${verificationData.vin}) does not match the vehicle VIN (${vehicle.vin}) on this deal.`);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch insurance verification VIN:', error);
      }
    }

    return warnings;
  };
  
  // User has clicked the OK button.
  const makePreChecksAndSetPostLoanDetailsBeforeClosing = async () => {
    setLoading(true);

    // Validate trade-in stock number with posting-level rules (AMS + local tables)
    setStockNumberError(null);
    const tradeInStockNumber = postLoanDetails?.tradeInStockNumber ?? currentApp.tradeInVehicle?.stockNumber;
    if (currentApp.tradeInVehicle && !hasValue(currentApp.tradeInVehicle.deletedDate) && hasValue(tradeInStockNumber)) {
      const validation = await validateStockNumber(tradeInStockNumber!, currentApp.tradeInVehicle.id, true);
      if (!validation.isAvailable) {
        setStockNumberError('Trade-in stock number already exists.');
        setLoading(false);
        return;
      }
    }

    const tradeInStockNumber2 = postLoanDetails?.tradeInStockNumber2 ?? currentApp.tradeInVehicle2?.stockNumber;
    if (currentApp.tradeInVehicle2 && !hasValue(currentApp.tradeInVehicle2.deletedDate) && hasValue(tradeInStockNumber2)) {
      const validation = await validateStockNumber(tradeInStockNumber2!, currentApp.tradeInVehicle2.id, true);
      if (!validation.isAvailable) {
        setStockNumberError('Trade-in 2 stock number already exists.');
        setLoading(false);
        return;
      }
    }

    const losCustomerIdAndAmsCustomerNumberPairs = getLosCustomerIdAndAmsCustomerNumberPairs(postLoanDetails!.customers);
    const amsCustomerPreCheckSearchResult = await getAmsCustomersForAmsCustomerNumbersAsync(losCustomerIdAndAmsCustomerNumberPairs, false, dispatch);
    const preCheckSearchResults = amsCustomerPreCheckSearchResult.filter(ac => hasValue(ac.existingAmsCustomerString) || hasValue(ac.existingLosCustomerString));
    setPreCheckSearchResults(preCheckSearchResults);

    if (preCheckSearchResults.length > 0) {
      setLoading(false);
      return;
    }

    // Check for insurance mismatch warnings before posting
    const mismatchWarnings = await getInsuranceMismatchWarnings();
    if (mismatchWarnings.length > 0) {
      setInsuranceMismatchMessages(mismatchWarnings);
      setShowInsuranceMismatchWarning(true);
      setLoading(false);
      return;
    }
    
    props.onOK(currentApp);
  };

  const handleInsuranceMismatchContinue = () => {
    setShowInsuranceMismatchWarning(false);
    setInsuranceMismatchMessages([]);
    props.onOK(currentApp);
  };
  
  const clearPostingDetailsAndCancel = () => {
    setPostLoanDetails(null);
    props.closeDialog();
  };

  const userCanSetCommission = activeUser
    ? props.configuration.featureFlags[FeatureFlagIds.UseCommission] && userHasPermission(activeUser, permissions.commission, PermissionSet.U)
    : false;
  
  const handleUpdateLoanCpiDetails = (loanCpiDetails: ICpiDetails | null, deferredDownCpiDetails: ICpiDetails | null, autoAccrueOneCpiPaymentWhenPosting: boolean) => {
    if (!postLoanDetails) {
      return;
    }
    
    setPostLoanDetails({
      ...postLoanDetails,
      loanCpiDetails: loanCpiDetails,
      deferredDownCpiDetails: deferredDownCpiDetails,
      autoAccrueOneCpiPaymentWhenPosting: autoAccrueOneCpiPaymentWhenPosting
    });
  };
  
  return (
    <div className={styles.dialogContainer}>
      {!!postLoanDetails &&
        <>
          <DialogTitle id="form-dialog-title">{props.title}</DialogTitle>
          <DialogContent className={styles.dialogContent}>
            <>
              <Input
                label={"AMS Account #"}
                valid={hasValue(postLoanDetails.accountNumber) && postLoanDetails.accountNumber!.length <= maxAmsAccountNumberLength}
                value={postLoanDetails.accountNumber}
                type='text'
                onChange={(e: InputChangeEvent) => handleAccountNumberUpdate(e.target.value as string)}
                style={{width: '50%'}}
                maxLength={maxAmsAccountNumberLength}
              />
              {!hasValue(postLoanDetails.accountNumber) &&
                <Error>{"Please enter an account number"}</Error>
              }
            </>
            <Divider variant="fullWidth" className={styles.divider}/>
            <CustomerNumberInputsDialog
              customers={postLoanDetails.customers ?? []}
              preCheckSearchResults={preCheckSearchResults}
              handleSelectCustomerNumberFromAms={handleSelectCustomerNumberFromAms}
              handleDesiredCustomerNumberUpdate={handleDesiredCustomerNumberUpdate}
              handleNameDifferenceAcknowledgement={handleNameDifferenceAcknowledgement}
            />
            <Divider variant="fullWidth" className={styles.divider}/>
            <div className={styles.columns}>
              {/*Left Column*/}
              <div className={styles.column}>
                <ComboBox
                  label={'Location of Sale'}
                  className={styles.input}
                  disabled
                  valid={hasValue(postLoanDetails.locationOfPaymentCode)}
                  data={getAmsLocationsOfSaleToDisplay(lookups.organizations)}
                  textField="description"
                  suggest={true}
                  clearButton={false}
                  value={selectedLocationOfSaleToDisplay}
                />
                <ComboBox
                  label={'Location of Payment'}
                  className={styles.input}
                  valid={hasValue(postLoanDetails?.locationOfPaymentCode)}
                  data={locationsOfPaymentsToDisplay}
                  textField="description"
                  suggest={true}
                  clearButton={false}
                  value={locationsOfPaymentsToDisplay.find(lop => lop.code === postLoanDetails.locationOfPaymentCode)}
                  onChange={(event: ComboBoxChangeEvent) => updateSelectedLocationOfPayment(event.value)}
                />
                <ComboBox
                  label={'Vehicle Location of Purchase'}
                  className={styles.input}
                  valid={hasValue(postLoanDetails.locationOfPurchaseId)}
                  data={locationsOfPurchaseToDisplay}
                  disabled={vehicleHasLocationOfPurchase} // If it's already on there, it just displays it
                  textField="description"
                  suggest={true}
                  clearButton={false}
                  value={locationsOfPurchaseToDisplay.find(lop => lop.id === postLoanDetails.locationOfPurchaseId)}
                  onChange={(event: ComboBoxChangeEvent) => updateSelectedLocationOfPurchase(event.value)}
                />
                <ComboBox
                  label={'Dealer Number'}
                  className={styles.input}
                  valid={hasValue(postLoanDetails.dealerNumberCode)}
                  data={amsDealerNumbersToDisplay}
                  textField="description"
                  suggest={true}
                  clearButton={false}
                  value={amsDealerNumbersToDisplay.find(dn => dn.code === postLoanDetails.dealerNumberCode)}
                  onChange={(event: ComboBoxChangeEvent) => updateSelectedDealerNumber(event.value)}
                />
                <ComboBox
                  label={'Late Fee'}
                  className={styles.input}
                  valid={hasValue(postLoanDetails.lateFeeCode)}
                  data={lateFeesToDisplay}
                  textField="description"
                  suggest={true}
                  clearButton={false}
                  value={lateFeesToDisplay.find(lf => lf.code === postLoanDetails.lateFeeCode)}
                  onChange={(event: ComboBoxChangeEvent) => updateSelectedLateFee(event.value)}
                />
                {hasDeferredDownPayments &&
                  <div>
                    <label className={styles.fieldLabel}>Apply late fee schedule to deferred down loan?</label>
                    <ComboBox
                      className={styles.input}
                      data={applyLateFeeToDeferredDownOptions}
                      textField="label"
                      suggest={true}
                      clearButton={false}
                      value={applyLateFeeToDeferredDownOptions.find(o => o.value === postLoanDetails.applyLateFeeScheduleToDeferredDown)}
                      onChange={(event: ComboBoxChangeEvent) => setPostLoanDetails({...postLoanDetails, applyLateFeeScheduleToDeferredDown: event.value?.value ?? false})}
                    />
                  </div>
                }
                <ComboBox
                  label={'Loan Code'}
                  className={styles.input}
                  valid={hasValue(postLoanDetails.loanCode)}
                  data={loanCodesToDisplay}
                  textField="description"
                  suggest={true}
                  clearButton={false}
                  value={loanCodesToDisplay.find(lc => lc.code === postLoanDetails.loanCode)}
                  onChange={(event: ComboBoxChangeEvent) => updateSelectedLoanCode(event.value)}
                />
                <ComboBox
                  label={'Loan Type'}
                  className={styles.input}
                  valid={hasValue(postLoanDetails.loanTypeCode)}
                  data={loanTypesToDisplay}
                  textField="description"
                  suggest={true}
                  clearButton={false}
                  value={loanTypesToDisplay.find(lt => lt.code === postLoanDetails.loanTypeCode)}
                  onChange={(event: ComboBoxChangeEvent) => updateSelectedLoanType(event.value)}
                />
                <ComboBox
                  label={'Loan Owner'}
                  className={styles.input}
                  valid={hasValue(postLoanDetails.loanOwnerCode)}
                  data={accountOwnersToDisplay}
                  textField="description"
                  suggest={true}
                  clearButton={false}
                  value={accountOwnersToDisplay.find(ao => ao.code === postLoanDetails.loanOwnerCode)}
                  onChange={(event: ComboBoxChangeEvent) => updateSelectedAccountOwner(event.value)}
                />
              </div>
              
              {/*Right Column*/}
              <div className={styles.column}>
                <ComboBox
                  label={'Interest Type'}
                  className={styles.input}
                  valid={hasValue(postLoanDetails.interestTypeCode)}
                  data={interestTypesToDisplay}
                  textField="description"
                  suggest={true}
                  clearButton={false}
                  value={interestTypesToDisplay.find(it => it.code === postLoanDetails.interestTypeCode)}
                  onChange={(event: ComboBoxChangeEvent) => updateSelectedInterestType(event.value)}
                />
                <DatePickerWithYearSanitizer
                  label={'Sales Log Date'}
                  className={styles.input}
                  valid={hasValue(postLoanDetails.salesLogDate)}
                  value={hasValue(postLoanDetails.salesLogDate) ? new Date(postLoanDetails!.salesLogDate!) : null}
                  onChange={(event: DatePickerChangeEvent) => updateSelectedSalesLogDate(event.value)}
                />
                <ComboBox
                  label={'Collector'}
                  className={styles.input}
                  valid={hasValue(postLoanDetails.collectorEmployeeNumber)}
                  data={amsUsersToDisplay}
                  textField="description"
                  suggest={true}
                  clearButton={false}
                  value={amsUsersToDisplay.find(au => au.code === postLoanDetails.collectorEmployeeNumber)}
                  onChange={(event: ComboBoxChangeEvent) => updateSelectedCollector(event.value)}
                />
                <ComboBox
                  label={'Advertising'}
                  className={styles.input}
                  valid={hasValue(postLoanDetails.advertisingCode)}
                  data={advertisingOptionsToDisplay}
                  textField="description"
                  suggest={true}
                  clearButton={false}
                  value={advertisingOptionsToDisplay.find(a => a.code === postLoanDetails.advertisingCode)}
                  onChange={(event: ComboBoxChangeEvent) => updateSelectedAdvertising(event.value)}
                />
                <ComboBox
                  label={'Portfolio'}
                  className={styles.input}
                  valid={hasValue(postLoanDetails.portfolioNumberCode)}
                  data={portfoliosToDisplay}
                  textField="description"
                  suggest={true}
                  clearButton={false}
                  value={portfoliosToDisplay.find(p => p.code === postLoanDetails.portfolioNumberCode)}
                  onChange={(event: ComboBoxChangeEvent) => updateSelectedPortfolio(event.value)}
                />
                <ComboBox
                  label={'Receipt Message'}
                  className={styles.input}
                  valid={hasValue(postLoanDetails.receiptMessageCode)}
                  data={receiptMessagesToDisplay}
                  textField="description"
                  suggest={true}
                  clearButton={false}
                  value={receiptMessagesToDisplay.find(p => p.code === postLoanDetails.receiptMessageCode)}
                  onChange={(event: ComboBoxChangeEvent) => updateSelectedReceiptMessage(event.value)}
                />
                {clientUsesPassTime &&
                  <>
                    <ComboBox
                      label={'Add To PassTime?'}
                      className={styles.input}
                      data={usePassTimeForLoanOptions}
                      textField="label"
                      suggest={true}
                      clearButton={false}
                      value={usePassTimeForLoanOptions.find(p => p.usePassTimeForLoan === usePassTimeForLoan)}
                      onChange={(event: ComboBoxChangeEvent) => updateUsePassTimeForLoan(event.value.usePassTimeForLoan)}
                    />
                    {usePassTimeForLoan &&
                      <>
                        <ComboBox
                          label={'PassTime Unit'}
                          valid={!usePassTimeForLoan || (usePassTimeForLoan && hasValue(postLoanDetails.passTimeUnitCombinedEnum))}
                          className={styles.input}
                          data={passTimeUnitTypes}
                          textField="label"
                          suggest={true}
                          clearButton={false}
                          value={passTimeUnitTypes.find(p => p.combinedTypeEnum === postLoanDetails.passTimeUnitCombinedEnum)}
                          onChange={(event: ComboBoxChangeEvent) => updatePassTimeUnitCombinedEnum(event.value)}
                        />
                        <Input
                          label={"PassTime Serial #"}
                          valid={!usingPassTimeWithoutSerialNumber}
                          value={postLoanDetails.passTimeSerialNumber}
                          type='text'
                          onChange={(e: InputChangeEvent) => updatePassTimeUnitSerialNumber((e.target.value as string).toUpperCase())}
                          className={styles.input}
                        />
                      </>
                    }
                  </>
              }
              </div>
            </div>
            {userCanSetCommission &&
              <>
                <Divider variant="fullWidth" className={styles.divider}/>
                <div className={styles.columns}>
                  <div className={styles.column}>
                    <ComboBox
                      label={'Salesperson 1'}
                      className={styles.input}
                      valid={salesPerson1AndCommissionAreValid}
                      data={amsUsersToDisplay}
                      textField="description"
                      suggest={true}
                      clearButton={true}
                      value={amsUsersToDisplay.find(au => au.code === postLoanDetails.salesman1EmployeeNumber)}
                      onChange={(event: ComboBoxChangeEvent) => updateSalesperson1(event.value)}
                    />
                    <ComboBox
                      label={'Salesperson 2'}
                      className={styles.input}
                      data={amsUsersToDisplay}
                      textField="description"
                      suggest={true}
                      clearButton={true}
                      value={amsUsersToDisplay.find(au => au.code === postLoanDetails.salesman2EmployeeNumber)}
                      onChange={(event: ComboBoxChangeEvent) => updateSalesperson2(event.value)}
                    />
                  </div>
                  <div className={styles.column}>
                    <NumericTextBox
                      label={"Sales Commission"}
                      style={{width: "100%"}}
                      value={postLoanDetails.salesman1CommissionAmount}
                      onChange={(e) => updateSalesperson1Commission(e.value ?? 0)}
                      valid={salesPerson1AndCommissionAreValid}
                      format="c2"
                      spinners={false}
                      className={styles.input}
                      min={0}
                    />
                  </div>
                </div>
              </>
            }
            {currentApp.tradeInVehicle && !hasValue(currentApp.tradeInVehicle.deletedDate) && (
              <>
                <Divider variant="fullWidth" className={styles.divider}/>
                <div>
                  <Input
                    label='Trade-In Stock #'
                    value={postLoanDetails.tradeInStockNumber ?? currentApp.tradeInVehicle?.stockNumber ?? ''}
                    onChange={(e: InputChangeEvent) => setPostLoanDetails({...postLoanDetails, tradeInStockNumber: e.value})}
                    maxLength={15}
                  />
                  {!hasValue(postLoanDetails.tradeInStockNumber ?? currentApp.tradeInVehicle?.stockNumber) &&
                    <Error>{"Stock # is required on the trade-in"}</Error>
                  }
                </div>
              </>
            )}
            {currentApp.tradeInVehicle2 && !hasValue(currentApp.tradeInVehicle2.deletedDate) && (
              <>
                <Divider variant="fullWidth" className={styles.divider}/>
                <div>
                  <Input
                    label='Trade-In 2 Stock #'
                    value={postLoanDetails.tradeInStockNumber2 ?? currentApp.tradeInVehicle2?.stockNumber ?? ''}
                    onChange={(e: InputChangeEvent) => setPostLoanDetails({...postLoanDetails, tradeInStockNumber2: e.value})}
                    maxLength={15}
                  />
                  {!hasValue(postLoanDetails.tradeInStockNumber2 ?? currentApp.tradeInVehicle2?.stockNumber) &&
                    <Error>{"Stock # is required on the trade-in"}</Error>
                  }
                </div>
              </>
            )}

          </DialogContent>
          <DialogActions>
            <div className={styles.errors}>
              {errors.map((error, key) => <Error key={key}>{error}</Error>)}
            </div>
            <Button
              style={{width: '150px'}}
              onClick={async () => await makePreChecksAndSetPostLoanDetailsBeforeClosing()}
              themeColor="primary"
              disabled={thereAreMultipleCustomersWithTheSameAmsCustomerNumber
                || atLeastOneCustomerDoesntHaveNumberEntered
                || !hasValue(postLoanDetails.accountNumber)
                || atLeastOneDropDownIsMissing
                || anAmsOrLosCustomerAlreadyExistsForADesiredNumber
                || errors.length > 0
                || (currentApp.tradeInVehicle && !hasValue(currentApp.tradeInVehicle.deletedDate) && !hasValue(postLoanDetails.tradeInStockNumber ?? currentApp.tradeInVehicle?.stockNumber))
                || (currentApp.tradeInVehicle2 && !currentApp.tradeInVehicle2.deletedDate && !hasValue(postLoanDetails.tradeInStockNumber2 ?? currentApp.tradeInVehicle2?.stockNumber))
            }
            >
              Post the Loan
            </Button>
            {clientUsesCpi &&
              <Button
                style={{minWidth: '120px'}}
                onClick={() => {
                  // If CPI hasn't been set yet in posting, prefill from insurance CPI if available
                  if (!postLoanDetails.loanCpiDetails) {
                    const cpiInsurance = currentApp.appCustomers
                      ?.flatMap(ac => ac.appCustomerVehicleInsurances ?? [])
                      .find(ins => ins.isCpi);
                    if (cpiInsurance) {
                      const toDate = (val: any): Date => {
                        if (!val) return new Date();
                        const d = val instanceof Date ? val : new Date(val);
                        return isNaN(d.getTime()) ? new Date() : d;
                      };
                      setPostLoanDetails({
                        ...postLoanDetails,
                        loanCpiDetails: {
                          factor: cpiInsurance.cpiFactor ?? defaultCpiFactorAmount,
                          startDate: toDate(cpiInsurance.cpiStartDate),
                          nextAccrueDate: toDate(cpiInsurance.cpiNextAccrueDate),
                          semiMonthlyPaymentDayOfMonth1: cpiInsurance.cpiSemi1DayOfMonth ?? 0,
                          semiMonthlyPaymentDayOfMonth2: cpiInsurance.cpiSemi2DayOfMonth ?? 0,
                        },
                      });
                    }
                  }
                  setShowCpiDialog(true);
                }}
                themeColor="secondary"
              >
                {!!postLoanDetails.loanCpiDetails ? "Update CPI" : "Set CPI"}
              </Button>
            }
            <Button 
              onClick={() => clearPostingDetailsAndCancel()}
              themeColor="light"
            >
              Cancel
            </Button>
          </DialogActions>
          {showCpiDialog && postLoanDetails && selectedFinanceQuote &&
            <AmsLoanCpiDialog
              postLoanDetails={postLoanDetails}
              updateCpiDetails={handleUpdateLoanCpiDetails}
              onClose={() => setShowCpiDialog(false)}
              defaultFactorAmount={defaultCpiFactorAmount}
              showOptionForDeferredDown={(selectedFinanceQuote.financeQuoteDeferredPayments?.length ?? 0) > 0}
              paymentFrequency={selectedFinanceQuote.paymentFrequency}
            />
          }
          <Backdrop open={loading}>
            <CircularProgress color="inherit"/>
          </Backdrop>
          {showInsuranceMismatchWarning && (
            <Window
              width={500}
              height={250}
              title="Insurance Mismatch Warning"
              onClose={() => setShowInsuranceMismatchWarning(false)}
            >
              <div style={{ padding: '10px' }}>
                {insuranceMismatchMessages.map((msg, idx) => (
                  <p key={idx} style={{ margin: '0 0 10px 0' }}>{msg}</p>
                ))}
                <p style={{ margin: '10px 0 0 0', fontStyle: 'italic' }}>
                  Do you want to continue posting anyway?
                </p>
              </div>
              <WindowActionsBar>
                <Button onClick={() => setShowInsuranceMismatchWarning(false)}>Go Back</Button>
                <Button themeColor="primary" onClick={handleInsuranceMismatchContinue}>Continue Anyway</Button>
              </WindowActionsBar>
            </Window>
          )}
        </>
      }
    </div>
  )
}